import { UndoRedo } from "../../lib/UndoRedo";
import { observe } from "../../lib/reactive";
import { Path } from "../../lib/runescape/pathing";
import { PathStepEntity } from "../ui/map/entities/PathStepEntity";
import { GameLayer } from "../../lib/gamemap/GameLayer";
import { util } from "../../lib/util/util";
var movement_state = Path.movement_state;
var copyUpdate = util.copyUpdate;
import { FakeLodash } from "../../lib/coreutil/FakeLodash";
export class PathBuilder {
    constructor(meta = {}, initial_value = []) {
        this.meta = meta;
        this.commit_lock = Promise.resolve();
        this.cursor = 0;
        this.committed_value = observe(null);
        this.cursor_state = observe(null);
        this.path = [];
        this.undoredo = new UndoRedo(state => this.setState(state));
        this.preview_layer = new GameLayer();
        this.commit(initial_value.length, initial_value);
    }
    assumptions() {
        return this.meta.start_state.assumptions;
    }
    setAssumptions(assumptions) {
        this.meta.start_state = movement_state.start(assumptions);
        this.commit(this.cursor, this.path, false);
    }
    async commit(cursor, path, save_state = true) {
        await this.commit_lock;
        this.commit_lock = (async () => {
            // Update cursor
            const length = path !== undefined ? path.length : this.path.length;
            this.cursor = Math.max(0, Math.min(length, cursor ?? this.cursor));
            if (path !== undefined) {
                this.path = path;
                const augmented = await Path.augment(this.path, this.meta.start_state, this.meta.target);
                const steps = augmented.steps.map((step, index) => {
                    return new PathBuilder.Step(this, index, step);
                });
                // Render previews
                {
                    const layer = new GameLayer();
                    steps.forEach(v => {
                        v.associated_preview = new PathStepEntity(v.step.raw)
                            .setInteractive()
                            .addTo(layer);
                    });
                    this.preview_layer.clearLayers().add(layer);
                }
                this.committed_value.set({
                    builder: this,
                    path: augmented,
                    steps: steps,
                });
            }
            if (save_state)
                this.saveState();
            this.cursor_state.set({
                state: Path.augmented.getState(this.committed_value.value().path, this.cursor),
                value: this.committed_value.value(),
                cursor: this.cursor
            });
        })();
    }
    setCursor(index) {
        this.commit(index, undefined, false);
        return this;
    }
    add(...steps) {
        this.commit(this.cursor + steps.length, copyUpdate(this.path, path => {
            path.splice(this.cursor, 0, ...steps);
        }));
        return this;
    }
    move(from, to) {
        if (from == to || from + 1 == to)
            return;
        let cursor_offset = 0;
        if (from >= this.cursor && to < this.cursor)
            cursor_offset = 1;
        if (from < this.cursor && to > this.cursor)
            cursor_offset = -1;
        this.commit(this.cursor + cursor_offset, copyUpdate(this.path, path => {
            const [removed] = path.splice(from, 1);
            if (from <= to)
                to -= 1;
            path.splice(to, 0, removed);
        }));
        return this;
    }
    delete(index) {
        if (index < 0 || index >= this.path.length)
            return this;
        const isBeforeCursor = index < this.cursor;
        this.commit(isBeforeCursor ? this.cursor - 1 : this.cursor, copyUpdate(this.path, path => {
            path.splice(index, 1);
        }));
        return this;
    }
    updateCopy(index, f) {
        const copy = FakeLodash.cloneDeep(this.path[index]);
        f(copy);
        return this.update(index, copy);
    }
    update(index, step) {
        this.commit(undefined, copyUpdate(this.path, path => {
            path[index] = FakeLodash.cloneDeep(step);
        }));
        return this;
    }
    async set(path, reset_history = false, cursor = undefined) {
        await this.commit_lock;
        if (reset_history) {
            this.undoredo.stack.reset();
        }
        this.commit(cursor ?? path.length, path);
        return this;
    }
    copyState() {
        return {
            cursor: this.cursor,
            path: FakeLodash.cloneDeep(this.path)
        };
    }
    saveState() {
        this.undoredo.stack.pushState(this.copyState());
    }
    async setState(state) {
        this.commit(state.cursor, state.path, false);
    }
    get() {
        return this.path;
    }
}
(function (PathBuilder) {
    class Step {
        constructor(parent, index, step) {
            this.parent = parent;
            this.index = index;
            this.step = step;
            this.associated_preview = null;
        }
        update(f) {
            this.parent.updateCopy(this.index, f);
        }
        delete() {
            this.parent.delete(this.index);
        }
    }
    PathBuilder.Step = Step;
})(PathBuilder || (PathBuilder = {}));
